// Toast — the transient-notification primitive, pure. A thin wrapper over
// @radix-ui/react-toast: Radix owns the hard parts (the ARIA live region,
// auto-dismiss timing, focus/keyboard reachability, swipe-to-dismiss); we own
// only the look against foundations v1 (.claude/research/design/06).
//
// PRESENTATIONAL ONLY — no store here. Whether a toast is showing is the
// consumer's state: `open` in, `onOpenChange` out (reported UP, never held
// internally). The imperative "enqueue a toast from anywhere" queue/store is
// an APP concern (it needs a mutable registry + ids + timers, all impurity),
// so it lives in the shell, not in this primitive. This file exports only the
// three presentational pieces — <ToastProvider>, <ToastViewport>, <Toast> —
// and the app composes them with its own queue.
//
// A toast is a small plane that genuinely floats above every other layer
// (--z-toast, the top of the ladder), so it wears the border→elevation
// discipline (§5): no resting edge, just the elevated ground + a soft shadow.
//
// `tone` is a plain string union (like ButtonVariant), not a meaning-swapping
// flag: it only tunes which redundant icon + which state hue the toast wears.
// TONE IS NEVER COLOUR-ALONE (SC 1.4.1): each tone ships a distinct built-in
// icon shape (check / triangle / octagon / info), so the tone reads from form
// for a colour-blind author, not from hue. Because the tone set is closed, the
// icon is baked in per tone rather than demanded as a prop.
//
// Pure/props-driven: `tone` picks the look; `title`/`description`/`action`
// are content passed straight through; state changes leave via `onOpenChange`.
// The only dep is the Lingui context (the dismiss button's accessible name via
// the `t` macro) — a context-provided dependency, not I/O.

import { useLingui } from "@lingui/react/macro";
import * as RadixToast from "@radix-ui/react-toast";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import "./Toast.css";

export type ToastTone = "neutral" | "success" | "warning" | "danger";

// Direction a toast can be flicked off-screen to dismiss. Named, not a loose
// string, so the gesture is typed on the public surface.
export type ToastSwipeDirection = "right" | "left" | "up" | "down";

export type ToastProps = {
    // The one-line headline. Required — a toast always announces something.
    readonly title: ReactNode;
    // Optional supporting line under the title.
    readonly description?: ReactNode;
    // Which redundant icon + state hue to wear. Defaults to neutral (no state
    // colour — an informational notice).
    readonly tone?: ToastTone;
    // An optional inline action (e.g. an "Undo" <Button>). Passed straight
    // through; the consumer owns its handler, keeping this primitive pure.
    readonly action?: ReactNode;
    // Controlled visibility. The consumer owns this — the toast never holds it.
    readonly open?: boolean;
    // Reported UP on every open/close (auto-dismiss, swipe, or the close
    // button), so the consumer's store stays the single source of truth.
    readonly onOpenChange?: (open: boolean) => void;
    // Auto-dismiss delay in ms. Omit to inherit the provider's default;
    // Radix pauses the timer on hover/focus, so this is a floor, not a cap.
    readonly duration?: number;
};

// Per-tone redundant icon (the non-colour channel, SC 1.4.1). Distinct SHAPES
// so tone reads from form: check = success, warning triangle = warning,
// crossed octagon = danger, info disc = neutral. aria-hidden — the title text
// carries the meaning for assistive tech; the icon only reinforces it.
const toneIcon: Record<ToastTone, ReactNode> = {
    neutral: <Icon name="info" />,
    success: <Icon name="check" />,
    warning: <Icon name="warning" />,
    danger: <Icon name="danger" />,
};

export const Toast = ({
    title,
    description,
    tone = "neutral",
    action,
    open,
    onOpenChange,
    duration,
}: ToastProps) => {
    const { t } = useLingui();
    return (
        <RadixToast.Root
            className={`bilda-toast bilda-toast--${tone}`}
            open={open}
            onOpenChange={onOpenChange}
            duration={duration}
        >
            <span className="bilda-toast__icon" aria-hidden="true">
                {toneIcon[tone]}
            </span>
            <div className="bilda-toast__body">
                <RadixToast.Title className="bilda-toast__title">
                    {title}
                </RadixToast.Title>
                {description ? (
                    <RadixToast.Description className="bilda-toast__description">
                        {description}
                    </RadixToast.Description>
                ) : null}
                {action ? (
                    <div className="bilda-toast__actions">{action}</div>
                ) : null}
            </div>
            <RadixToast.Close
                className="bilda-toast__close"
                aria-label={t`Dismiss`}
            >
                {/* The ONE registry close form (P33). The tone icons above
                    stay bespoke — the danger octagon has no registry twin. */}
                <Icon name="close" />
            </RadixToast.Close>
        </RadixToast.Root>
    );
};

export type ToastProviderProps = {
    readonly children: ReactNode;
    // Default auto-dismiss for every toast under this provider (ms). A single
    // toast can override via its own `duration`.
    readonly duration?: number;
    // Which way a toast flicks off to dismiss. Defaults to "right" (the
    // conventional dismiss direction for a bottom-corner viewport).
    readonly swipeDirection?: ToastSwipeDirection;
};

// Mount once at the app root, wrapping the whole tree. Radix uses it to own
// the shared live region and swipe config across every toast.
export const ToastProvider = ({
    children,
    duration = 5000,
    swipeDirection = "right",
}: ToastProviderProps) => (
    <RadixToast.Provider duration={duration} swipeDirection={swipeDirection}>
        {children}
    </RadixToast.Provider>
);

// The fixed region toasts stack into (a Radix-managed <ol> — one live region,
// keyboard-reachable via F8). Place once, near the app root. It positions
// itself; the consumer supplies nothing.
export const ToastViewport = () => {
    const { t } = useLingui();
    return (
        <RadixToast.Viewport
            className="bilda-toast-viewport"
            label={t`Notifications`}
        />
    );
};
